import React, { useContext, useEffect, useRef, useState } from 'react';
import AgreementResult from '../../../models/AgreementResult';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../../core/button';
import { signNewAgreement } from './service';
import styles from './sign_agreement.module.scss';
import { Agreement } from '../../../models/Agreement';
import Notification from '../../core/notification';
import { trimImage } from '../../../utils/image';
import { AgreementMetadata } from '../../../models/AgreementMetadata';
import { sleep } from '../../../utils';
import { Busy } from '../../core/busy';
import { SalesContext } from '../../../SalesContext';
import Product from '../../../models/Product';
import { ImgAgreement } from './ImgAgreement';

export type SignAgreementProps = {
  /**
   * Agreement to sign.
   */
  agreement?: AgreementResult;
  meta: AgreementMetadata;
  subscriptions?: Product[];
  listEnergySummary?: boolean;
  /**
   * Whether to create an automated signature instead of requiring the user to
   * sign manually.
   */
  automatic?: boolean;

  shouldConfirmCity?: boolean;

  photo?: Blob;
  usePdfRenderer: boolean;
  useScrollIndicator?: boolean;
  onSigned: (agreement: Agreement) => void;
  onDeclined?: (agreement: Agreement) => void;
};

/**
 * Renders an agreement and a way of signing it.
 */
export const SignAgreement: React.FC<SignAgreementProps> = ({
  meta,
  subscriptions,
  agreement,
  automatic = false,
  useScrollIndicator,
  usePdfRenderer,
  photo,
  onSigned,
}) => {
  if (!agreement && (!subscriptions || !meta?.creditCheckResult)) {
    throw new Error("Must provide either an unsigned agreement or subscriptions and creditCheckResult");
  }

  if (typeof useScrollIndicator !== 'boolean') {
    useScrollIndicator = automatic;
  }

  const { setOfferSignatureUri, offerSignatureUri, monthlyPaymentTotal } = useContext(SalesContext);

  /**
   * Pre-loads a signature font so that is available for signature canvas.
   */
  function preloadSignatureFont() {
    const element = document.createElement('span');
    element.innerText = 'Laddar font';
    element.style.position = 'absolute';
    element.style.pointerEvents = 'none';
    element.style.opacity = '0';
    element.style.fontFamily = 'Allura';
    document.body.appendChild(element);
  }

  const canvasRef = useRef<SignatureCanvas>(null);
  const [signBusy, setSignBusy] = useState(false);
  const [begunSignature, setBegunSignature] = useState(false);
  const [canvasData, setCanvasData] = useState<any[][]>([[]]);
  const [showCanvas, setShowCanvas] = useState<boolean>(true);

  async function sign(retries = 2) {
    if (signBusy) {
      return;
    }

    setSignBusy(true);
    const parsedOfferSignature = await trimImage(canvasRef.current.getTrimmedCanvas().toDataURL());
    setOfferSignatureUri(parsedOfferSignature);

    let error: any;
    for (let i = 0; i < retries + 1; i++) {
      try {
        var signedAgreement = await signNewAgreement(
          subscriptions,
          meta?.bundles,
          meta.creditCheckResult,
          { offerSignatureUri: parsedOfferSignature, signatureMethod: "draw", monthlyPaymentTotal },
          photo
        );
      } catch (e) {
        console.error(e);
        error = e;
        await sleep(1500);
      }
    }

    if (signedAgreement) {
      Notification.show("Signerat avtal");
      onSigned(signedAgreement);
    }

    if (error?.message === 'canceled') {
      Notification.show("Avtalet är återkallad");
    } else if (error) {
      Notification.show("Signering misslyckades, prova igen senare");
    }

    setSignBusy(false);
  }

  useEffect(() => {
    if (automatic) {
      preloadSignatureFont();
    }
  }, [automatic]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <div className={styles.viewport} style={{ height: 0 /* Gör så att den nedre delen inte hamnar utanför skärmen. */ }}>
        {
          !usePdfRenderer && agreement.uri ? (
            <ImgAgreement
              agreement={agreement}
              useScrollIndicator={useScrollIndicator}
            />
          ) : agreement.uri ? (
            <iframe
              style={{ border: 0, borderRadius: 6, flexGrow: 1 }}
              width="100%"
              title='Avtal'
              height="100%"
              src={`${agreement.uri}#toolbar=0&navpanes=0&embedded=true`}
            />
          ) : (
            <div className={styles.busyContainer}>
              <Busy />
              <p>Hämtar avtal</p>
            </div>
          )
        }
      </div>

      <div className={styles.signContainer}>
        {!usePdfRenderer ? <p style={{ fontSize: "16px", marginBottom: "1rem" }}>Genom att signera avtalet så godkänner jag Alektias allmänna villkor.</p> : null}

        <div style={{ position: 'relative' }}>
          {!signBusy ?
            <>
              {showCanvas ?
                <>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: "1rem", gap: "8px" }}>
                    <Button onClick={() => setShowCanvas(false)}>Göm ritplatta</Button>
                    <Button onClick={() => {
                      canvasRef.current.clear();
                      setCanvasData([[]]);
                    }}>Rensa</Button>

                    {!offerSignatureUri ?
                      <Button weight='primary' onClick={() => {
                        setCanvasData([[]]);
                      }} disabled={!canvasData[0].length} busy={signBusy} onClickCapture={() => sign()}>Signera avtal</Button> : null}
                  </div>
                  <SignatureCanvas
                    ref={canvasRef}
                    onBegin={() => {
                      if (!begunSignature) {
                        setBegunSignature(true);
                      }
                      setCanvasData(canvasRef.current?.toData())
                    }}
                    canvasProps={{
                      width: 800,
                      height: 360
                    }}
                  />

                  <svg className={styles.baseline} width="75%" height="2px">
                    <line x1="0" y1="0" x2="100%" y2="100%" strokeWidth="2" strokeDasharray="8 4" stroke="rgba(0, 0, 0, 0.25)" />
                  </svg>

                  {
                    !begunSignature ? (
                      <h3 className={styles.hint}>Signera här</h3>
                    ) : null
                  }
                </> : <Button onClick={() => setShowCanvas(true)}>Visa ritplatta</Button>
              }
            </> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", padding: "5rem" }}><Busy /> Signerar avtal</div>}
        </div>
      </div>
    </div>
  );
}